/**
 * `AriaLive` — a visually-hidden polite ARIA live region for asynchronous
 * announcements from within the help-docs-panel.
 *
 * Used by {@link ./ChatbotDock | ChatbotDock} to satisfy Requirements 8.2
 * and 8.5:
 *
 *  - On open, the dock announces "Chatbot opened. Type your question."
 *  - On close, the dock announces "Chatbot closed".
 *
 * The region is rendered with `role="status"` (matching the implicit role
 * of an `aria-live="polite"` container) and `aria-atomic="true"` so screen
 * readers announce the full message each time it changes rather than
 * diffing character-by-character.
 *
 * Ownership: this component is a passive presentation layer — parents
 * drive announcements by re-rendering with a new `message`. No internal
 * state, no side effects.
 *
 * @see .kiro/specs/help-docs-panel/requirements.md — Requirements 8.2, 8.5
 * @packageDocumentation
 */
import * as React from "react";

/**
 * Props accepted by {@link AriaLive}.
 */
export interface AriaLiveProps {
  /**
   * The message to announce. When the empty string, the region renders
   * empty and no announcement occurs. Updating this prop to a new
   * non-empty value re-triggers a polite screen-reader announcement.
   */
  message: string;
}

/**
 * Renders a visually-hidden polite ARIA live region whose content is
 * `message`.
 *
 * @remarks
 * - Uses Tailwind's `sr-only` utility to hide the region visually while
 *   keeping it in the accessibility tree.
 * - Uses `role="status"` explicitly so `screen.getByRole("status")` in
 *   tests finds the region without relying on implicit role mapping.
 * - Uses `aria-atomic="true"` so the whole message is announced on any
 *   change rather than only the diff.
 */
export function AriaLive(props: AriaLiveProps): JSX.Element {
  const { message } = props;
  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="sr-only"
    >
      {message}
    </div>
  );
}
